import $ from 'jquery'
import _ from 'lodash'
import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { Router } from 'react-router-dom'
import { browserHistory } from 'browser/history'

import 'browser/components/atomic-elements/organisms/overlay-manager/_overlay-manager.scss'

class OverlayManager {
  private overlays: any[]
  private overlayContextProvider: React.ComponentType<{children: React.ReactNode}> | null = null

  constructor() {
    this.overlays = []
  }

  public closeAllOverlays() {
    this.overlays.forEach((envelope) => {
      envelope.root.unmount()
      // HTMLElement.remove is not supported in IE11
      $(envelope.portalContainer).remove()
    })
    this.overlays = []
  }

  public closeOverlay(overlay) {
    const envelope = this.getPortalEnvelope(overlay)
    if (!envelope) { return }
    envelope.root.unmount()
    // HTMLElement.remove is not supported in IE11
    $(envelope.portalContainer).remove()
    _.pull(this.overlays, envelope)
  }

  public getNextStackingOrder() {
    const stackingOrders = _.map(this.overlays, 'stackingOrder') as number[]
    return _.isEmpty(stackingOrders) ? 0 : _.max(stackingOrders) + 1
  }

  public getPortalContainer(overlay) {
    const envelope = this.getPortalEnvelope(overlay)
    return _.get(envelope, 'portalContainer')
  }

  public getPortalEnvelope(overlay) {
    return _.find(this.overlays, (item) => item.overlay === overlay)
  }

  public isTopOfStack(overlay) {
    const envelope = this.getPortalEnvelope(overlay)
    if (!envelope) { return false }
    const currentStackingOrders = _.map(this.overlays, 'stackingOrder')
    const maxStackingOrder = Math.max.apply(null, currentStackingOrders)
    return envelope.stackingOrder === maxStackingOrder
  }

  public openOverlay(Component, props) {
    this.openOverlayWithParent(null, Component, props)
  }

  public openOverlayElement(element, callback?): Element {
    return this.openOverlayElementWithParent(null, element, callback)
  }

  public updateOverlayElement(overlay, element, callback?) {
    return this.updateOverlayElementWithParent(null, overlay, element, callback)
  }

  public openOverlayWithParent(_parent, Component, props) {
    const modalElement = <Component {...props} />
    return this.openOverlayElementWithParent(null, modalElement)
  }

  public openOverlayElementWithParent(_parent, element, callback?): Element {
    const portalContainer = document.createElement('div')
    const stackingOrder = this.getNextStackingOrder()
    const stackingOrderClass = `u-zIndex-8-${stackingOrder}`
    portalContainer.setAttribute('class', `c-portalContainer ${stackingOrderClass}`)
    this.rootPortalContainer.appendChild(portalContainer)
    const { overlay, root } = this.renderOverlay(portalContainer, stackingOrder, element, callback)
    const envelope = { overlay, root, portalContainer, stackingOrder }
    this.overlays.push(envelope)
    return overlay
  }

  // No-ops kept for API compatibility — context inheritance via renderSubtreeIntoContainer
  // was removed in React 18; overlays now render in their own createRoot.
  public popReactHost() { /* no-op */ }
  public pushReactHost(_reactHost) { /* no-op */ }

  public setOverlayContextProvider(provider: React.ComponentType<{children: React.ReactNode}>) {
    this.overlayContextProvider = provider
  }

  public clearOverlayContextProvider() {
    this.overlayContextProvider = null
  }

  public updateOverlayElementWithParent(_parent, overlay, element, callback?) {
    const envelope = this.getPortalEnvelope(overlay)
    if (envelope) {
      const { portalContainer, stackingOrder, root } = envelope
      envelope.overlay = this.renderOverlay(
        portalContainer, stackingOrder, element, callback, root).overlay
      return envelope.overlay
    }
  }

  private get rootPortalContainer() {
    return document.getElementById('root-portal-container')
  }

  private renderOverlay(domNode, stackingOrder, element, callback?, existingRoot?: Root): { overlay: Element; root: Root } {
    const elementOnClose = element.props.onClose
    const onClose = () => {
      this.closeOverlay(domNode)
      if (elementOnClose) {
        elementOnClose()
      }
    }
    element = React.cloneElement(element, { onClose, stackingOrder })
    const ContextProvider = this.overlayContextProvider
    const root = existingRoot ?? createRoot(domNode)
    root.render(
      <Router history={browserHistory}>
        {ContextProvider ? <ContextProvider>{element}</ContextProvider> : element}
      </Router>
    )
    if (callback) { setTimeout(callback, 0) }
    return { overlay: domNode, root }
  }
}

const OverlayManagerInst = new OverlayManager()
export default OverlayManagerInst
